import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowRightLeft, Vault, Banknote } from "lucide-react";
import { toast } from "sonner";

export default function Safe() {
  const [inv, setInv] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [cash, setCash] = useState({ balance: 0, ledger: [] });
  const [categories, setCategories] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [transferItem, setTransferItem] = useState(null);

  const load = useCallback(() => {
    api.get("/safe/inventory").then((r) => setInv(r.data)).catch(() => {});
    api.get("/safe/transfers").then((r) => setTransfers(r.data)).catch(() => {});
    api.get("/safe/cash").then((r) => setCash(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl">
      <Tabs defaultValue="inventory">
        <TabsList>
          <TabsTrigger value="inventory" data-testid="tab-safe-inventory"><Vault className="w-4 h-4 mr-1.5" />Safe Inventory</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-safe-transfers"><ArrowRightLeft className="w-4 h-4 mr-1.5" />Transfers</TabsTrigger>
          <TabsTrigger value="cash" data-testid="tab-safe-cash"><Banknote className="w-4 h-4 mr-1.5" />Safe Cash</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="space-y-4">
          <div className="flex justify-end"><Button onClick={() => setAddOpen(true)} className="bg-indigo-600" data-testid="add-safe-btn"><Plus className="w-4 h-4 mr-1.5" />Add to safe</Button></div>
          <Card className="divide-y">
            {inv.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3" data-testid={`safe-row-${s.id}`}>
                <div className="flex-1">
                  <div className="font-medium text-slate-800">{s.name} <span className="text-xs text-slate-400">· {s.category}</span></div>
                  <div className="text-xs text-slate-500">{(s.total_grams || s.quantity || 0).toFixed(1)} {s.unit || "g"} · ${(s.price_per_gram || 0).toFixed(2)}/g · cost {money(s.cost)}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setTransferItem(s)} data-testid={`transfer-btn-${s.id}`}><ArrowRightLeft className="w-4 h-4 mr-1.5" />Transfer</Button>
              </div>
            ))}
            {inv.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Safe is empty. Add product to the safe first.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="transfers">
          <Card className="divide-y">
            {transfers.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`transfer-row-${t.id}`}>
                <div className="flex-1"><span className="font-medium">{t.product_name}</span> · {t.quantity} units</div>
                <span className="text-xs text-slate-500">{t.source} → {t.destination}</span>
                <span className="text-xs text-slate-400">{t.by} · {fmtDate(t.at)}</span>
              </div>
            ))}
            {transfers.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No transfers yet.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="cash"><SafeCash cash={cash} reload={load} /></TabsContent>
      </Tabs>

      <SafeAddDialog open={addOpen} setOpen={setAddOpen} categories={categories} onSaved={load} />
      {transferItem && <TransferDialog item={transferItem} onClose={() => setTransferItem(null)} onSaved={() => { setTransferItem(null); load(); }} />}
    </div>
  );
}

function SafeCash({ cash, reload }) {
  const [type, setType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const submit = () => api.post("/safe/cash", { type, amount: +amount, note }).then(() => { setAmount(""); setNote(""); reload(); toast.success("Recorded"); }).catch((e) => toast.error(formatApiError(e)));
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Card className="p-5 md:col-span-1">
        <div className="text-xs text-slate-500 uppercase tracking-wide">Safe Cash Balance</div>
        <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="safe-cash-balance">{money(cash.balance)}</div>
        <div className="mt-4 space-y-2">
          <Select value={type} onValueChange={setType}><SelectTrigger data-testid="safe-cash-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="deposit">Deposit</SelectItem><SelectItem value="withdraw">Withdraw</SelectItem><SelectItem value="count">Count / Set</SelectItem></SelectContent>
          </Select>
          <Input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="safe-cash-amount" />
          <Input placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button onClick={submit} className="w-full" data-testid="safe-cash-submit">Record</Button>
        </div>
      </Card>
      <Card className="p-0 md:col-span-2 divide-y max-h-[420px] overflow-auto">
        {cash.ledger.map((l) => (
          <div key={l.id} className="flex justify-between items-center p-3 text-sm">
            <div><span className="capitalize font-medium">{l.type}</span> <span className="text-xs text-slate-400">{l.note}</span></div>
            <div className="text-right"><div className={l.type === "withdraw" ? "text-red-600" : "text-emerald-600"}>{l.type === "withdraw" ? "-" : "+"}{money(l.amount)}</div><div className="text-xs text-slate-400">Bal {money(l.balance_after)}</div></div>
          </div>
        ))}
        {cash.ledger.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No cash movements yet.</div>}
      </Card>
    </div>
  );
}

function SafeAddDialog({ open, setOpen, categories, onSaved }) {
  const empty = { name: "", brand: "", category: "", total_grams: 0, quantity: 0, unit: "grams", price_per_gram: 0, cost: 0, retail_price: 0, strain_type: "", thc: 0, thca: 0, description: "" };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post("/safe/inventory", { ...f, total_grams: +f.total_grams, quantity: +f.quantity, price_per_gram: +f.price_per_gram, cost: +f.cost, retail_price: +f.retail_price, thc: +f.thc, thca: +f.thca });
      toast.success("Added to safe"); setF(empty); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>Add product to Safe</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="safe-name-input" /></F>
          <F label="Brand"><Input value={f.brand} onChange={(e) => set("brand", e.target.value)} /></F>
          <F label="Category">
            <Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="safe-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
          </F>
          <F label="Strain type"><Input value={f.strain_type} onChange={(e) => set("strain_type", e.target.value)} /></F>
          <F label="Total grams / qty"><Input type="number" value={f.total_grams} onChange={(e) => { set("total_grams", e.target.value); set("quantity", e.target.value); }} data-testid="safe-grams-input" /></F>
          <F label="Price per gram"><Input type="number" value={f.price_per_gram} onChange={(e) => set("price_per_gram", e.target.value)} data-testid="safe-ppg-input" /></F>
          <F label="Total cost"><Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="safe-cost-input" /></F>
          <F label="Retail price"><Input type="number" value={f.retail_price} onChange={(e) => set("retail_price", e.target.value)} /></F>
          <F label="THC %"><Input type="number" value={f.thc} onChange={(e) => set("thc", e.target.value)} /></F>
          <F label="THCA %"><Input type="number" value={f.thca} onChange={(e) => set("thca", e.target.value)} /></F>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-safe-btn">Add to safe</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ item, onClose, onSaved }) {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const avail = item.total_grams || item.quantity || 0;
  const save = () => api.post("/safe/transfer", { safe_item_id: item.id, quantity: +qty, note }).then(() => { toast.success("Transferred to Grove ST"); onSaved(); }).catch((e) => toast.error(formatApiError(e)));
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Transfer to Grove ST floor</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{item.name} · {avail.toFixed(1)} available in safe</p>
        <F label="Quantity to transfer"><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="transfer-qty-input" autoFocus /></F>
        <F label="Note"><Input value={note} onChange={(e) => setNote(e.target.value)} /></F>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="confirm-transfer-btn">Transfer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => <div className="space-y-1"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
